import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Loader2, AlertTriangle, CheckCircle2, Info, Inbox } from "lucide-react";
import { fmtEUR, fmtSigned } from "@/lib/format";

export const CARD = "bg-white rounded-3xl border border-[#EEF0F2] shadow-[0_1px_2px_rgba(15,46,37,0.04),0_6px_18px_rgba(15,46,37,0.04)]";

export function Money({ value, className = "", decimals = false }) {
  return <span className={className}>{fmtEUR(value)}</span>;
}

export function Trend({ value, suffix = "" }) {
  const pos = Number(value) >= 0;
  const Icon = pos ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
        pos ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {fmtSigned(value)} {suffix}
    </span>
  );
}

const TONE = {
  Bonne: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  "À surveiller": { c: "bg-amber-50 text-amber-800 border-amber-200", Icon: Info },
  Tendue: { c: "bg-orange-50 text-orange-800 border-orange-200", Icon: AlertTriangle },
  Critique: { c: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertTriangle },
};

export function HealthBadge({ level }) {
  const t = TONE[level] || TONE["À surveiller"];
  const { Icon } = t;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-bold ${t.c}`}>
      <Icon className="h-4 w-4" /> {level}
    </span>
  );
}

export function ProgressBar({ value, color = "emerald", height = "h-3" }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const bg = { emerald: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500", blue: "bg-blue-500" }[color];
  return (
    <div className={`w-full ${height} rounded-full bg-gray-100 overflow-hidden`}>
      <motion.div
        className={`${height} rounded-full ${bg}`}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    </div>
  );
}

export function Label({ children, className = "" }) {
  return (
    <p className={`text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400 ${className}`}>{children}</p>
  );
}

export function Loader({ label = "Chargement…" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-gray-400">
      <Loader2 className="h-7 w-7 animate-spin" />
      <p className="mt-3 text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({ title, subtitle, action }) {
  return (
    <div className={`${CARD} flex flex-col items-center justify-center py-16 text-center px-6`}>
      <div className="rounded-2xl bg-gray-50 p-4">
        <Inbox className="h-8 w-8 text-gray-300" />
      </div>
      <p className="mt-4 font-display text-lg font-bold">{title}</p>
      {subtitle && <p className="mt-1 max-w-sm text-sm text-gray-500">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorRetry({ onRetry, title = "Impossible de charger cette page" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="section-error">
      <div className="rounded-2xl bg-rose-50 p-4">
        <AlertTriangle className="h-8 w-8 text-rose-500" />
      </div>
      <p className="mt-4 font-display text-lg font-bold">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-gray-500">Vérifiez votre connexion puis réessayez.</p>
      <button
        data-testid="section-retry"
        onClick={onRetry}
        className="mt-5 rounded-full bg-[#065F46] px-6 py-2.5 font-semibold text-white hover:bg-[#047857]"
      >
        Réessayer
      </button>
    </div>
  );
}

export function Stagger({ children, className = "" }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="show"
      variants={{ show: { transition: { staggerChildren: 0.06 } } }}
    >
      {children}
    </motion.div>
  );
}

export function Item({ children, className = "" }) {
  return (
    <motion.div
      className={className}
      variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}
